import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { commonMenuLabels } from "@/i18n";
import { ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface NavigationProps {
  isMobile?: boolean;
  onClose?: () => void;
}

const Navigation = ({ isMobile = false, onClose }: NavigationProps) => {
  const { language } = useLanguage();
  const menuLabels = commonMenuLabels(language);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100); // Small delay to prevent accidental closing
  };

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (isMobile) {
    return (
      <div className="py-1">
        <Link
          to="/"
          className="block w-full px-4 py-2.5 text-left text-base hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={onClose}
        >
          {menuLabels.listings}
        </Link>
        <Link
          to="/hur-det-fungerar"
          className="block w-full px-4 py-2.5 text-left text-base hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={onClose}
        >
          {menuLabels.howItWorks}
        </Link>
        <Link
          to="/salj-bilen"
          className="block w-full px-4 py-2.5 text-left text-base hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={onClose}
        >
          {menuLabels.sellCar}
        </Link>
        <Link
          to="/kop-bil"
          className="block w-full px-4 py-2.5 text-left text-base hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={onClose}
        >
          {menuLabels.buyCar}
        </Link>
      </div>
    );
  }

  const DropdownContent = () => (
    <div
      className="fixed w-48 bg-white dark:bg-zinc-950 border border-gray-200 dark:border-white/15 rounded-md shadow-xl z-[100]"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="py-1">
        <Link
          to="/"
          className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={() => setIsOpen(false)}
        >
          {menuLabels.listings}
        </Link>
        <Link
          to="/hur-det-fungerar"
          className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={() => setIsOpen(false)}
        >
          {menuLabels.howItWorks}
        </Link>
        <Link
          to="/salj-bilen"
          className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={() => setIsOpen(false)}
        >
          {menuLabels.sellCar}
        </Link>
        <Link
          to="/kop-bil"
          className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-white/10"
          onClick={() => setIsOpen(false)}
        >
          {menuLabels.buyCar}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="flex items-center space-x-6">
      <div
        ref={dropdownRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button className="flex items-center hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-sm">
          {menuLabels.menu} <ChevronDown className="ml-0.5 h-3 w-3" />
        </button>
        {isOpen && createPortal(<DropdownContent />, document.body)}
      </div>
    </div>
  );
};

export default Navigation;
